import { useMemo, useCallback, useRef, Suspense } from 'react';
import { useUIContext } from '../components/_UIProvider.mjs';
export { N as NextUIProvider } from './NextUIProvider-client-5vYOZ8C-.mjs';
import { jsx } from 'react/jsx-runtime';

const isSafari = ()=>{
    if (typeof window === "undefined" || !window.navigator) return false;
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
};

const useNavigate = ({ shallow } = {})=>{
    const { navigation: { push, replace } } = useUIContext();
    const getNavigate = useMemo(()=>{
        const browser = {
            push: (url)=>window.history.pushState({}, "", url.toString()),
            replace: (url)=>window.history.replaceState({}, "", url.toString())
        };
        const next = {
            push: push,
            replace: replace
        };
        return shallow ? browser : next;
    }, [
        shallow,
        push,
        replace
    ]);
    return getNavigate;
};

/**
 * Reactive URLSearchParams hook that allows for updating the URL
 */ const useQueryParams = ({ replace = false, shallow = true, scroll = true } = {})=>{
    const { navigation: { useSearchParams } } = useUIContext();
    const searchParams = useSearchParams();
    const searchParamString = searchParams.toString();
    const navigator = useNavigate({
        shallow
    });
    const getNavigate = useCallback(()=>{
        return replace ? navigator.replace : navigator.push;
    }, [
        replace,
        navigator
    ]);
    /**
   * Batching layer
   */ const batchedActionsRef = useRef(new Set());
    const isBatchingRef = useRef(false);
    const commit = useCallback(()=>{
        if (typeof window === "undefined") {
            if (process.env.NODE_ENV !== "production") {
                console.warn("useQueryParams: Tried to set search params on the server, this is a no-op.");
            }
            return;
        }
        if (batchedActionsRef.current.size > 0) {
            const newParams = new URLSearchParams(window.location.search);
            batchedActionsRef.current.forEach((action)=>{
                switch(action.type){
                    case "set":
                        newParams.set(action.key, action.value);
                        break;
                    case "delete":
                        newParams.delete(action.key, action.value);
                        break;
                    case "append":
                        newParams.append(action.key, action.value);
                        break;
                }
            });
            const navigate = getNavigate();
            navigate(`${window.location.pathname}?${newParams.toString()}`, {
                scroll
            });
            batchedActionsRef.current.clear();
        }
        isBatchingRef.current = false;
    }, [
        searchParamString,
        getNavigate
    ]);
    const batchUpdate = useCallback((action)=>{
        if (!isBatchingRef.current) {
            isBatchingRef.current = true;
            batchedActionsRef.current.add(action);
            queueMicrotask(commit);
        } else {
            batchedActionsRef.current.add(action);
        }
    }, [
        commit
    ]);
    // Override the set, delete, and clear methods to update the URL
    const set = useCallback((key, value)=>{
        batchUpdate({
            type: "set",
            key,
            value
        });
    }, [
        batchUpdate
    ]);
    const deleteMethod = useCallback((key, value)=>{
        batchUpdate({
            type: "delete",
            key,
            value
        });
    }, [
        batchUpdate
    ]);
    const append = useCallback((key, value)=>{
        batchUpdate({
            type: "append",
            key,
            value
        });
    }, [
        batchUpdate
    ]);
    const proxiedSearchParams = useMemo(()=>{
        return new Proxy(searchParams, {
            get (target, prop, receiver) {
                switch(prop){
                    case "has":
                        const has = Reflect.get(target, prop, searchParams);
                        if (isSafari()) {
                            return (key, value)=>{
                                const allParams = searchParams.getAll(key);
                                return value ? allParams.includes(value) : allParams.length > 0;
                            };
                        }
                        return has.bind(searchParams);
                    case "set":
                        return set;
                    case "delete":
                        return deleteMethod;
                    case "append":
                        return append;
                    default:
                        const value = Reflect.get(target, prop, searchParams);
                        if (typeof value === "function") {
                            return value.bind(searchParams);
                        }
                        return value;
                }
            }
        });
    }, [
        searchParams,
        set,
        deleteMethod,
        append
    ]);
    return proxiedSearchParams;
};
const useUrlSearchParams = useQueryParams;

function withSearchParams(WrappedComponent, config) {
    const displayName = WrappedComponent.displayName || WrappedComponent.name || "Component";
    const ComponentWithSearchParams = (props)=>{
        const searchParams = useUrlSearchParams(config);
        return /*#__PURE__*/ jsx(WrappedComponent, {
            ...props,
            searchParams: searchParams
        });
    };
    const ComponentWithTheme = (props)=>{
        return /*#__PURE__*/ jsx(Suspense, {
            fallback: /*#__PURE__*/ jsx(WrappedComponent, {
                ...props,
                searchParams: new URLSearchParams()
            }),
            children: /*#__PURE__*/ jsx(ComponentWithSearchParams, {
                ...props
            })
        });
    };
    ComponentWithTheme.displayName = `withSearchParams(${displayName})`;
    return ComponentWithTheme;
}

export { useQueryParams, useUrlSearchParams, withSearchParams };
